import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import MovieCard from '@/components/base/MovieCard';

interface MovieScrollRowProps {
  movies: Movie[];
  morePath?: string;
}

export default function MovieScrollRow({ movies, morePath = '/category/latest' }: MovieScrollRowProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (direction: 1 | -1) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.min(node.clientWidth * 0.8, 720), behavior: 'smooth' });
  };

  return (
    <>
      {/* mobile: single column vertical list */}
      <div className="grid grid-cols-1 gap-2.5 md:hidden">
        {movies.slice(0, 8).map((movie) => (
          <Link
            key={movie.id}
            to={`/detail/${movie.id}`}
            className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5"
            aria-label={`查看${movie.title}详情`}
          >
            <div className="relative aspect-[2/3] w-[76px] shrink-0 overflow-hidden rounded-md bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title}封面`}
                title={`${movie.title} 大雷影视详情介绍`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1 py-0.5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-medium text-foreground-950">{movie.title}</h3>
                <span className="flex shrink-0 items-center gap-0.5 font-label text-sm font-semibold text-accent-600">
                  <i className="ri-star-fill text-[11px]" />
                  {movie.rating.toFixed(1)}
                </span>
              </div>
              <p className="mt-1 truncate text-xs text-foreground-400">
                {movie.year} · {movie.region} · {movie.duration}
              </p>
              <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">{movie.summary}</p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {movie.genres.slice(0, 2).map((genre) => (
                  <span key={genre} className="rounded bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500">
                    {genre}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
        <Link
          to={morePath}
          className="mt-1 flex cursor-pointer items-center justify-center gap-1 rounded-md border border-background-200 py-2.5 text-xs text-foreground-500"
        >
          查看全部作品
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      {/* desktop: horizontal scroll row */}
      <div className="relative hidden md:block">
        <div
          ref={scrollerRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2"
        >
          {movies.map((movie) => (
            <div key={movie.id} className="w-[166px] shrink-0 snap-start lg:w-[182px]">
              <MovieCard movie={movie} />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="向左滚动"
          className="absolute -left-4 top-[38%] z-10 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 lg:flex"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="向右滚动"
          className="absolute -right-4 top-[38%] z-10 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 lg:flex"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </>
  );
}